/* eslint-disable react/prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import { TextTableCell } from 'components/table';
import { connect } from 'react-redux';
import { TablePagination, Tooltip } from '@material-ui/core';
import { fetchMembersByUrl } from 'store/members/members-actions';
import Icon from 'components/icon';
import Button from 'components/button';
import Box from 'components/box';
import HouseholdMembersModal from './household-members-modal';
import { Alert } from 'components/alerts';
import { Text } from 'components/text';
import session from 'lib/session';
import { apiConfig } from 'lib/config';

class IncorrectMembersTable extends Component {
  static mapDispatchToProps = dispatch => ({
    fetchMoreMembers: url => dispatch(fetchMembersByUrl(url)),
  });

  state = {
    members: null,
    showHouseholdModal: false,
    selectedMemberId: null,
    selectedMemberIds: new Set(),
    isDeleting: false,
    deleteError: null,
  }

  componentDidMount() {
    // eslint-disable-next-line react/destructuring-assignment
    const { members } = this.props.data;
    this.setState({ members });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      const { members } = this.props.data;
      this.setState({ members, selectedMemberIds: new Set() });
    }
  }

  handleChangePage = (event, newPage) => {
    const { fetchMoreMembers, data } = this.props;
    const { page, prevUrl, nextUrl } = data;
    const pageToBeFetched = newPage + 1;

    if (pageToBeFetched > page) {
      fetchMoreMembers(nextUrl);
    }

    if (pageToBeFetched < page) {
      fetchMoreMembers(prevUrl);
    }
  }

  handleRowClick = (memberId, event) => {
    // Don't navigate if clicking on buttons or checkboxes
    if (event && (event.target.closest('button') || event.target.closest('a') || event.target.closest('input[type="checkbox"]'))) {
      return;
    }
    const { history } = this.props;
    history.push(`/members/${memberId}`);
  }

  handleSelectAll = (event) => {
    const { members } = this.state;
    if (event.target.checked) {
      const allIds = new Set(members.map(m => m.memberId));
      this.setState({ selectedMemberIds: allIds });
    } else {
      this.setState({ selectedMemberIds: new Set() });
    }
  }

  handleSelectMember = (memberId, event) => {
    event.stopPropagation();
    const { selectedMemberIds } = this.state;
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    this.setState({ selectedMemberIds: newSelected });
  }

  handleDetailsClick = (memberId, event) => {
    event.stopPropagation();
    this.setState({ showHouseholdModal: true, selectedMemberId: memberId });
  }

  handleDeleteClick = async (memberId, event) => {
    event.stopPropagation();
    
    const confirmMessage = `Are you sure you want to delete this household and all related records? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    this.setState({ isDeleting: true, deleteError: null });

    try {
      const tokenWithExpiry = session.getTokenWithExpiry();
      const baseUrl = apiConfig.BASE_URL;
      const response = await fetch(`${baseUrl}/incorrect_members/bulk_delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${tokenWithExpiry.token}`,
        },
        body: JSON.stringify({ member_ids: [memberId] }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Delete failed: ${response.statusText}`);
      }

      // Refresh the data
      this.setState({ 
        isDeleting: false,
        deleteError: null,
      });

      // Reload the page data - trigger a refetch
      const { fetchMoreMembers, data } = this.props;
      if (data.prevUrl || data.nextUrl) {
        // Refresh current page
        fetchMoreMembers(data.nextUrl || data.prevUrl);
      } else {
        // Force a reload by navigating away and back
        window.location.reload();
      }
    } catch (error) {
      this.setState({ 
        isDeleting: false, 
        deleteError: error.message || 'Failed to delete member. Please try again.' 
      });
    }
  }

  handleBulkDelete = async () => {
    const { selectedMemberIds } = this.state;
    if (selectedMemberIds.size === 0) {
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedMemberIds.size} household(s) and all related records? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    this.setState({ isDeleting: true, deleteError: null });

    try {
      const tokenWithExpiry = session.getTokenWithExpiry();
      const baseUrl = apiConfig.BASE_URL;
      const response = await fetch(`${baseUrl}/incorrect_members/bulk_delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${tokenWithExpiry.token}`,
        },
        body: JSON.stringify({ member_ids: Array.from(selectedMemberIds) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Delete failed: ${response.statusText}`);
      }

      // Refresh the data by calling the parent component's refresh function
      // For now, just clear selection and show success
      this.setState({ 
        selectedMemberIds: new Set(), 
        isDeleting: false,
        deleteError: null,
      });

      // Reload the page data - trigger a refetch
      const { fetchMoreMembers, data } = this.props;
      if (data.prevUrl || data.nextUrl) {
        // Refresh current page
        fetchMoreMembers(data.nextUrl || data.prevUrl);
      } else {
        // Force a reload by navigating away and back
        window.location.reload();
      }
    } catch (error) {
      this.setState({ 
        isDeleting: false, 
        deleteError: error.message || 'Failed to delete members. Please try again.' 
      });
    }
  }

  handleCloseModal = () => {
    this.setState({ showHouseholdModal: false, selectedMemberId: null });
  }

  render() {
    const { data } = this.props;
    const { total, page, limit = 100 } = data;
    const { members, selectedMemberIds, isDeleting, deleteError } = this.state;
    const numSelected = selectedMemberIds.size;
    const allSelected = members && members.length > 0 && numSelected === members.length;

    return (
      <>
        {deleteError && (
          <Box marginBottom={3}>
            <Alert type="error">{deleteError}</Alert>
          </Box>
        )}
        {numSelected > 0 && (
          <Box flex alignItems="center" marginBottom={3} padding={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <Box marginRight={3}>
              <Text fontSize={3}>{numSelected} member(s) selected</Text>
            </Box>
            <Button
              primaryRed
              small
              onClick={this.handleBulkDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : `Delete ${numSelected} Selected`}
            </Button>
          </Box>
        )}
        <Table>
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '3%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '24%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={numSelected > 0 && !allSelected}
                  checked={allSelected && members && members.length > 0}
                  onChange={this.handleSelectAll}
                  inputProps={{ 'aria-label': 'select all members' }}
                />
              </TableCell>
              <TableCell padding="checkbox">Full Name</TableCell>
              <TableCell padding="checkbox" />
              <TableCell padding="checkbox">Membership Number</TableCell>
              <TableCell padding="checkbox">Gender</TableCell>
              <TableCell padding="checkbox">Membership Status</TableCell>
              <TableCell padding="checkbox">Membership Type</TableCell>
              <TableCell padding="checkbox">Household Size</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members && members.map(member => {
              const isSelected = selectedMemberIds.has(member.memberId);
              return (
                <TableRow
                  key={member.memberId}
                  hover
                  onClick={(e) => this.handleRowClick(member.memberId, e)}
                  style={{ cursor: 'pointer' }}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isSelected}
                      onChange={(e) => this.handleSelectMember(member.memberId, e)}
                      inputProps={{ 'aria-label': `select ${member.fullName}` }}
                    />
                  </TableCell>
                  <TextTableCell padding="checkbox" color="blue.5" style={{ whiteSpace: 'nowrap' }}>{member.fullName}</TextTableCell>
                  <TextTableCell padding="checkbox" color="red.5">
                    {member.archivedReason && (
                      <Tooltip title={member.archivedReason}>
                        <Icon name="archived" size={15} iconSize={15} />
                      </Tooltip>
                    )}
                  </TextTableCell>
                  <TextTableCell padding="checkbox" style={{ whiteSpace: 'nowrap' }}>{member.membershipNumber}</TextTableCell>
                  <TextTableCell padding="checkbox" style={{ whiteSpace: 'nowrap' }}>{member.gender}</TextTableCell>
                  <TextTableCell padding="checkbox" style={{ whiteSpace: 'nowrap' }}>{member.membershipStatus}</TextTableCell>
                  <TextTableCell padding="checkbox" style={{ whiteSpace: 'nowrap' }}>{member.paying}</TextTableCell>
                  <TextTableCell padding="checkbox" style={{ whiteSpace: 'nowrap' }}>
                    <Box flex alignItems="center">
                      <span style={{ marginRight: '8px' }}>{member.householdSize}</span>
                      <Box flex>
                        <Box marginRight={1}>
                          <Button
                            small
                            inline
                            onClick={(e) => this.handleDetailsClick(member.memberId, e)}
                          >
                            Details
                          </Button>
                        </Box>
                        <Button
                          small
                          inline
                          primaryRed
                          onClick={(e) => this.handleDeleteClick(member.memberId, e)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </Button>
                      </Box>
                    </Box>
                  </TextTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {members && (
          <TablePagination
            rowsPerPageOptions={false}
            component="div"
            count={total}
            rowsPerPage={limit}
            page={page - 1}
            onChangePage={this.handleChangePage}
          />
        )}
        {this.state.showHouseholdModal && this.state.selectedMemberId && (
          <HouseholdMembersModal
            memberId={this.state.selectedMemberId}
            onRequestClose={this.handleCloseModal}
          />
        )}
      </>
    );
  }
}

export default connect(null, IncorrectMembersTable.mapDispatchToProps)(withRouter(IncorrectMembersTable));

IncorrectMembersTable.propTypes = {
  history: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    members: PropTypes.array,
    total: PropTypes.number,
    page: PropTypes.number,
    limit: PropTypes.number,
    prevUrl: PropTypes.string,
    nextUrl: PropTypes.string,
  }).isRequired,
};
